import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const API = `${API_URL}/api`;

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // v1.2.66 — Only logout on 401 (token actually invalid). Transient
      // failures — network drop, 5xx, timeout, tab backgrounded mid-flight,
      // browser suspending the tab — must NOT clear the session. Users
      // returning to a backgrounded tab were being silently kicked out
      // because v1.2.65's presence write pushed /auth/me latency into a
      // range where any transient failure now cascaded into logout().
      console.error('Failed to fetch user:', error);
      if (error.response?.status === 401) {
        logout();
      }
      // Non-401 (network / 5xx / timeout): keep the session, keep the
      // cached user (if any), let the next authenticated call retry.
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchCurrentUser]);

  const login = async (email, password, totp_token) => {
    // Final-stop normalization in case any caller bypassed the
    // page-level cleanup. The backend is also case-insensitive on email
    // lookups, but every layer in the auth path should agree on the
    // canonical form to keep audit logs consistent.
    const normalizedEmail = (email || '').trim().toLowerCase();
    const payload = { email: normalizedEmail, password };
    if (totp_token) payload.totp_token = totp_token;
    const response = await axios.post(`${API}/auth/login`, payload);
    const data = response.data;

    // MFA required — return signal to caller, don't set session yet
    if (data.mfa_required) {
      return { mfa_required: true, mfa_pending_user_id: data.mfa_pending_user_id, email };
    }

    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    // v1.1.82 — Mirror the JWT into a browser cookie so the admin-gated
    // Swagger UI / OpenAPI spec endpoints (/api/docs, /api/redoc,
    // /api/openapi.json) can authenticate plain-browser GET requests
    // (which don't carry an Authorization header). same-site=strict scopes
    // it tightly; max-age=86400 matches the JWT TTL.
    document.cookie = `fmiq_session=${data.token}; path=/; samesite=strict; max-age=86400`;
    return data.user;
  };

  const register = async (email, password, full_name, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, full_name, role });
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    document.cookie = `fmiq_session=${token}; path=/; samesite=strict; max-age=86400`;
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    // v1.1.82 — Clear the Swagger session cookie on logout.
    document.cookie = 'fmiq_session=; path=/; samesite=strict; max-age=0';
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, API, refreshUser: fetchCurrentUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
